import { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  InputAdornment,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  Alert,
  ListItemIcon,
  Button,
  CircularProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import Sidebar from '../../Pages/landingPage/Sidebar';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SearchIcon from '@mui/icons-material/Search';
import CloseRounded from '@mui/icons-material/CloseRounded';
import KeyboardArrowDownRounded from '@mui/icons-material/KeyboardArrowDownRounded';
import Fade from '@mui/material/Fade';
import CheckRounded from '@mui/icons-material/CheckRounded';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import Business from '@mui/icons-material/Business';
import EditIcon from '../../Images/Edit.png';
import {
  fetchUsers,
  getOrganizationById,
  getOrganizations,
} from '../../apis/Admin';
// import DeleteIcon from '../../Images/Delete.png';
import {
  Add,
  Person4,
  // PersonOutline,
  SettingsRounded,
  // DashboardRounded,
  TrackChanges,
  Announcement,
  ContactMail,
  RateReview,
} from '@mui/icons-material';
import PropTypes from 'prop-types';
import Cookies from 'js-cookie';
import UserDialog from '../UserDialog';
import AssignOrganizationDialog from '../AssignOrganizationDialog';
import { AdminTabs } from '../AdminTabs';
import { useTimezone } from '../../hooks/useTimezone';

const containerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, staggerChildren: 0.1 },
  },
};
const itemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.4, type: 'spring', stiffness: 100 },
  },
};
const rowVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.05, duration: 0.3, ease: 'easeOut' },
  }),
};

// const formatDate = (ts) => {
//   const date = new Date(ts);

//   // Check if the date is invalid
//   if (isNaN(date.getTime())) {
//     return 'Invalid Date';
//   }

//   return date
//     .toISOString()
//     .replace('T', ' ')
//     .replace(/\.\d+Z$/, ' UTC');
// };

export default function AdminUsers() {
  // eslint-disable-next-line
  const navigate = useNavigate();
  const { formatDate } = useTimezone();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState({
    open: false,
    mode: 'create',
    user: null,
  });

  const [assignOrgDialog, setAssignOrgDialog] = useState({
    open: false,
    user: null,
  });

  const [pageError, setPageError] = useState(null);
  const [orgSeatInfo, setOrgSeatInfo] = useState(null);
  const [loadingSeatInfo, setLoadingSeatInfo] = useState(false);

  // Super admin check
  const rawRole = useMemo(
    () => Cookies.get('Role_id') ?? Cookies.get('role_id'),
    [],
  );

  const isSuperAdmin = useMemo(() => {
    const n = Number(rawRole);
    return !Number.isNaN(n) && n === 1;
  }, [rawRole]);

  const isAdmin = useMemo(() => {
    const n = Number(rawRole);
    return !Number.isNaN(n) && (n === 2 || n === 1);
  }, [rawRole]);

  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);

  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [orgFilter, setOrgFilter] = useState('all');
  const [organizations, setOrganizations] = useState([]);

  // const [editOpen, setEditOpen] = useState(false);

  const [sortConfig, setSortConfig] = useState({
    column: null,
    direction: 'none',
  });

  const fetchUsersData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const usersData = await fetchUsers(token);
      const mappedUsers = usersData.map((u) => ({
        ...u,
        createdAt: u.created_at ? new Date(u.created_at).getTime() : 0,
      }));
      setUsers(mappedUsers);
      setPageError(null);
    } catch (error) {
      console.error('Error fetching users:', error);
      setPageError('Error fetching user data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsersData();
  }, []);

  // Fetch organizations for Super Admin filter
  useEffect(() => {
    if (!isSuperAdmin) return;

    const fetchOrgs = async () => {
      try {
        const orgs = await getOrganizations();
        setOrganizations(orgs);
      } catch (error) {
        console.error('Failed to fetch organizations:', error);
      }
    };

    fetchOrgs();
  }, [isSuperAdmin]);

  // Fetch organization seat info for Admin users
  const fetchOrgSeatInfo = async () => {
    if (!isAdmin || isSuperAdmin) return; // Only for regular Admin, not Super Admin

    const orgId = Cookies.get('Org_id');
    if (!orgId) return;

    setLoadingSeatInfo(true);
    try {
      const orgData = await getOrganizationById(orgId);
      setOrgSeatInfo({
        total_seats: orgData.total_seats || 0,
        used_seats: orgData.used_seats || 0,
        available_seats:
          orgData.available_seats !== undefined
            ? orgData.available_seats
            : orgData.total_seats === 0
              ? -1
              : (orgData.total_seats || 0) - (orgData.used_seats || 0),
        org_name: orgData.org_name || '',
      });
    } catch (error) {
      console.error('Failed to fetch organization seat info:', error);
      // Don't show error to user, just silently fail
    } finally {
      setLoadingSeatInfo(false);
    }
  };

  useEffect(() => {
    fetchOrgSeatInfo();
  }, [isAdmin, isSuperAdmin]);

  const handleSort = (column) => {
    let direction = 'asc';
    if (sortConfig.column === column && sortConfig.direction === 'asc')
      direction = 'desc';
    else if (sortConfig.column === column && sortConfig.direction === 'desc') {
      direction = 'none';
      column = null;
    }
    setSortConfig({ column, direction });
  };

  const applySort = (rows) => {
    if (sortConfig.direction === 'none' || !sortConfig.column) return rows;

    const arr = [...rows];

    return arr.sort((a, b) => {
      let A;
      let B;

      switch (sortConfig.column) {
        case 'name': {
          // Use first_name + last_name instead of a.name (since you don't have name)
          const aName = `${a.first_name || ''} ${a.last_name || ''}`;
          const bName = `${b.first_name || ''} ${b.last_name || ''}`;
          A = aName.trim().toLowerCase();
          B = bName.trim().toLowerCase();
          break;
        }

        case 'role': {
          A = (a.role_name || '').toLowerCase();
          B = (b.role_name || '').toLowerCase();
          break;
        }

        case 'status': {
          // is_active is boolean, don't call toLowerCase on it
          A = a.is_active ? 'active' : 'inactive';
          B = b.is_active ? 'active' : 'inactive';
          break;
        }

        case 'createdAt': {
          A = typeof a.createdAt === 'number' ? a.createdAt : 0;
          B = typeof b.createdAt === 'number' ? b.createdAt : 0;
          break;
        }

        case 'trial_status': {
          const aTrial = a.trial_end_date
            ? new Date(a.trial_end_date) > new Date()
              ? 1
              : 2
            : 3;
          const bTrial = b.trial_end_date
            ? new Date(b.trial_end_date) > new Date()
              ? 1
              : 2
            : 3;
          A = aTrial;
          B = bTrial;
          break;
        }

        default:
          return 0;
      }

      if (
        sortConfig.column === 'createdAt' ||
        sortConfig.column === 'trial_status'
      ) {
        return sortConfig.direction === 'asc' ? A - B : B - A;
      }

      // Safety: ensure they're strings before localeCompare
      const strA = String(A);
      const strB = String(B);

      return sortConfig.direction === 'asc'
        ? strA.localeCompare(strB)
        : strB.localeCompare(strA);
    });
  };

  const filtered = useMemo(() => {
    const safeUsers = Array.isArray(users) ? users : [];

    const q = search.trim().toLowerCase();
    const roleFilterNorm = (roleFilter || 'all').toLowerCase();
    const statusFilterNorm = (statusFilter || 'all').toLowerCase();

    const matchesQuery = (u) => {
      if (!q) return true;

      const firstName = (u.first_name || '').toLowerCase();
      const lastName = (u.last_name || '').toLowerCase();
      const email = (u.email || '').toLowerCase();
      const role = (u.role_name || '').toLowerCase();
      const orgName = (u.org_name || u.Org_name || '').toLowerCase();

      return (
        firstName.includes(q) ||
        lastName.includes(q) ||
        email.includes(q) ||
        role.includes(q) ||
        orgName.includes(q)
      );
    };

    const rows = safeUsers.filter((u) => {
      const roleName = (u.role_name || '').toLowerCase();
      const statusValue = u.is_active ? 'active' : 'inactive';

      const roleMatches =
        roleFilterNorm === 'all' || roleName === roleFilterNorm;

      const statusMatches =
        statusFilterNorm === 'all' || statusValue === statusFilterNorm;

      const orgMatches =
        !orgFilter || // when nothing selected
        orgFilter === 'all' || // "All" option
        (orgFilter === 'none' && !u.org_name) || // "No Organization" option
        u.org_name === orgFilter;

      return roleMatches && statusMatches && orgMatches && matchesQuery(u);
    });

    return applySort(rows);
  }, [users, search, roleFilter, statusFilter, orgFilter, sortConfig]);

  const displayed = showAll ? filtered : filtered.slice(0, 10);

  const Pill = ({ children, color }) => (
    <Box
      sx={{
        display: 'inline-block',
        px: 1.25,
        py: 0.35,
        borderRadius: '10px',
        fontSize: '0.75rem',
        fontWeight: 600,
        letterSpacing: '0.02em',
        color,
        bgcolor: alpha(color, 0.12),
        textTransform: 'capitalize',
      }}
    >
      {children}
    </Box>
  );

  Pill.propTypes = {
    children: PropTypes.node.isRequired,
    color: PropTypes.string.isRequired,
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          bgcolor: '#f8fafc',
          minHeight: '100vh',
        }}
      >
        <Sidebar />
        <Box sx={{ flexGrow: 1, px: 3, py: 2 }}>
          <Collapse in={!!pageError} unmountOnExit>
            <Alert
              severity="error"
              onClose={() => setPageError(null)}
              sx={{
                mb: 2,
                border: '1px solid #fecaca',
                background: '#fee2e2',
                color: '#7f1d1d',
              }}
            >
              {pageError}
            </Alert>
          </Collapse>

          <AdminTabs
            items={[
              ...(isSuperAdmin
                ? [
                    {
                      to: '/admin/organizations',
                      label: 'Organizations',
                      icon: Business,
                    },
                  ]
                : []),
              { to: '/admin/user', label: 'User Management', icon: Person4 },
              {
                to: '/admin/annoucement',
                label: 'Annoucement',
                icon: Announcement,
              },
              {
                to: '/admin/setting',
                label: 'Application Settings',
                icon: SettingsRounded,
              },
              {
                to: '/admin/audit-log',
                label: 'Audit Logs',
                icon: TrackChanges,
              },
              ...(isSuperAdmin
                ? [
                    {
                      to: '/admin/contact-submissions',
                      label: 'Contact Submissions',
                      icon: ContactMail,
                    },
                    {
                      to: '/admin/public-reviews',
                      label: 'Public Reviews',
                      icon: RateReview,
                    },
                  ]
                : []),
            ]}
          />

          {/* Seat Information Card for Admin Users */}
          {isAdmin && !isSuperAdmin && orgSeatInfo && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Box
                sx={{
                  mt: 2,
                  mb: 2,
                  p: 2.5,
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  background:
                    'linear-gradient(135deg, #F8FAFF 0%, #EEF2FF 100%)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 2,
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        color: '#374151',
                        mb: 1,
                      }}
                    >
                      Organization Seat Information
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                      <Box>
                        <Typography
                          sx={{
                            fontSize: '0.75rem',
                            color: '#6b7280',
                            mb: 0.5,
                          }}
                        >
                          Total Seats
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '1rem',
                            fontWeight: 600,
                            color: '#111827',
                          }}
                        >
                          {orgSeatInfo.total_seats === 0
                            ? 'Unlimited'
                            : orgSeatInfo.total_seats}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography
                          sx={{
                            fontSize: '0.75rem',
                            color: '#6b7280',
                            mb: 0.5,
                          }}
                        >
                          Used Seats
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '1rem',
                            fontWeight: 600,
                            color: '#111827',
                          }}
                        >
                          {orgSeatInfo.used_seats}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography
                          sx={{
                            fontSize: '0.75rem',
                            color: '#6b7280',
                            mb: 0.5,
                          }}
                        >
                          Available Seats
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '1rem',
                            fontWeight: 600,
                            color:
                              orgSeatInfo.available_seats === -1
                                ? '#6b7280'
                                : orgSeatInfo.available_seats > 0
                                  ? '#2e7d32'
                                  : orgSeatInfo.available_seats === 0
                                    ? '#ed6c02'
                                    : '#d32f2f',
                          }}
                        >
                          {orgSeatInfo.available_seats === -1
                            ? 'Unlimited'
                            : orgSeatInfo.available_seats}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  {orgSeatInfo.available_seats !== -1 &&
                    orgSeatInfo.available_seats <= 2 &&
                    orgSeatInfo.available_seats >= 0 && (
                      <Alert
                        severity={
                          orgSeatInfo.available_seats === 0 ? 'warning' : 'info'
                        }
                        sx={{
                          flex: 1,
                          minWidth: 250,
                          '& .MuiAlert-message': {
                            fontSize: '0.875rem',
                          },
                        }}
                      >
                        {orgSeatInfo.available_seats === 0
                          ? 'No seats available. Please contact Super Admin to increase seat limit.'
                          : `Only ${orgSeatInfo.available_seats} seat${
                              orgSeatInfo.available_seats === 1 ? '' : 's'
                            } remaining. Contact Super Admin if you need more.`}
                      </Alert>
                    )}
                </Box>
              </Box>
            </motion.div>
          )}

          {isAdmin && !isSuperAdmin && loadingSeatInfo && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                py: 2,
              }}
            >
              <CircularProgress size={24} sx={{ color: '#5965A6' }} />
            </Box>
          )}

          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <Box
              component={motion.div}
              sx={{
                borderRadius: '4px',
                background: '#ffffff',
                border: '1px solid #e2e8f0',
                p: 0,
                mt: 1,
                mb: 3,
                position: 'relative',
                overflow: 'hidden',
                boxShadow:
                  '0 1px 3px 0 rgba(0,0,0,0.1), 0 1px 2px 0 rgba(0,0,0,0.06)',
              }}
            >
              <motion.div variants={itemVariants}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 2.5,
                    borderBottom: '1px solid #e2e8f0',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Box sx={{ mb: 1.5 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.25,
                        }}
                      >
                        {/* <Box
                          sx={{
                            width: 4,
                            height: 26,
                            bgcolor: '#5965A6',
                            borderRadius: 1,
                          }}
                        /> */}
                        <Typography
                          component="h2"
                          sx={{
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            fontWeight: 600,
                            color: '#111827',
                            letterSpacing: '0.03em',
                            lineHeight: 1.2,
                          }}
                        >
                          User Management
                        </Typography>
                      </Box>
                    </Box>

                    {/* Filters: Role + Status */}
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <InputLabel sx={{ fontSize: '0.875rem' }}>
                        Role
                      </InputLabel>
                      <Select
                        value={roleFilter}
                        onChange={(e) => {
                          setRoleFilter(e.target.value);
                          setShowAll(false);
                        }}
                        label="Role"
                        IconComponent={KeyboardArrowDownRounded}
                        MenuProps={{
                          TransitionComponent: Fade,
                          PaperProps: {
                            sx: {
                              mt: 0.5,
                              borderRadius: '10px',
                              border: '1px solid #e5e7eb',
                              boxShadow: '0 10px 24px rgba(0,0,0,0.12)',
                              overflow: 'hidden',
                            },
                          },
                          MenuListProps: { dense: true, sx: { py: 0.5 } },
                        }}
                        sx={{
                          fontSize: '0.875rem',
                          borderRadius: '10px',
                          bgcolor: '#fff',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#e2e8f0',
                            transition:
                              'border-color .2s ease, box-shadow .2s ease',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#5965A6',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#8E98CD',
                            boxShadow: '0 0 0 2px rgba(89,101,166,0.14)',
                          },
                          '& .MuiSelect-select': {
                            py: 0.75,
                            px: 1.25,
                            fontSize: '0.75rem',
                          },
                          '& .MuiSelect-icon': {
                            color: '#94a3b8',
                            transition: 'transform .2s ease',
                          },
                          '&.Mui-expanded .MuiSelect-icon': {
                            transform: 'rotate(180deg)',
                          },
                        }}
                      >
                        {[
                          { v: 'all', label: 'All' },
                          { v: 'admin', label: 'Admin' },
                          { v: 'user', label: 'User' },
                        ].map((opt) => (
                          <MenuItem
                            key={opt.v}
                            value={opt.v}
                            sx={{
                              fontSize: '0.875rem',
                              px: 1.25,
                              py: 0.75,
                              '&:hover': { backgroundColor: '#F3F6FF' },
                              '&.Mui-selected, &.Mui-selected:hover': {
                                backgroundColor: '#EEF2FF',
                                color: '#5965A6',
                                fontWeight: 600,
                              },
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: 24,
                                color: '#5965A6',
                                visibility:
                                  roleFilter === opt.v ? 'visible' : 'hidden',
                              }}
                            >
                              <CheckRounded fontSize="small" />
                            </ListItemIcon>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {/* Organization Filter - Only for Super Admin */}
                    {isSuperAdmin && (
                      <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel sx={{ fontSize: '0.875rem' }}>
                          Organization
                        </InputLabel>
                        <Select
                          value={orgFilter}
                          onChange={(e) => {
                            setOrgFilter(e.target.value);
                            setShowAll(false);
                          }}
                          label="Organization"
                          IconComponent={KeyboardArrowDownRounded}
                          MenuProps={{
                            TransitionComponent: Fade,
                            PaperProps: {
                              sx: {
                                mt: 0.5,
                                borderRadius: '10px',
                                border: '1px solid #e5e7eb',
                                boxShadow: '0 10px 24px rgba(0,0,0,0.12)',
                                overflow: 'hidden',
                              },
                            },
                            MenuListProps: { dense: true, sx: { py: 0.5 } },
                          }}
                          sx={{
                            fontSize: '0.875rem',
                            borderRadius: '10px',
                            bgcolor: '#fff',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#e2e8f0',
                              transition:
                                'border-color .2s ease, box-shadow .2s ease',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#5965A6',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#8E98CD',
                              boxShadow: '0 0 0 2px rgba(89,101,166,0.14)',
                            },
                            '& .MuiSelect-select': {
                              py: 0.75,
                              px: 1.25,
                              fontSize: '0.75rem',
                            },
                            '& .MuiSelect-icon': {
                              color: '#94a3b8',
                              transition: 'transform .2s ease',
                            },
                            '&.Mui-expanded .MuiSelect-icon': {
                              transform: 'rotate(180deg)',
                            },
                          }}
                        >
                          <MenuItem
                            value="all"
                            sx={{
                              fontSize: '0.875rem',
                              px: 1.25,
                              py: 0.75,
                              '&:hover': { backgroundColor: '#F3F6FF' },
                              '&.Mui-selected, &.Mui-selected:hover': {
                                backgroundColor: '#EEF2FF',
                                color: '#5965A6',
                                fontWeight: 600,
                              },
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: 24,
                                color: '#5965A6',
                                visibility:
                                  orgFilter === 'all' ? 'visible' : 'hidden',
                              }}
                            >
                              <CheckRounded fontSize="small" />
                            </ListItemIcon>
                            All
                          </MenuItem>
                          <MenuItem
                            value="none"
                            sx={{
                              fontSize: '0.875rem',
                              px: 1.25,
                              py: 0.75,
                              '&:hover': { backgroundColor: '#F3F6FF' },
                              '&.Mui-selected, &.Mui-selected:hover': {
                                backgroundColor: '#EEF2FF',
                                color: '#5965A6',
                                fontWeight: 600,
                              },
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: 24,
                                color: '#5965A6',
                                visibility:
                                  orgFilter === 'none' ? 'visible' : 'hidden',
                              }}
                            >
                              <CheckRounded fontSize="small" />
                            </ListItemIcon>
                            No Organization
                          </MenuItem>
                          {organizations.map((org) => {
                            const orgName = (
                              org.org_name ||
                              org.name ||
                              ''
                            ).trim();
                            if (!orgName) return null; // skip orgs without a name

                            return (
                              <MenuItem
                                key={org.org_id || org.id || orgName}
                                value={orgName} // ✅ value is org_name
                                sx={{
                                  fontSize: '0.875rem',
                                  px: 1.25,
                                  py: 0.75,
                                  '&:hover': { backgroundColor: '#F3F6FF' },
                                  '&.Mui-selected, &.Mui-selected:hover': {
                                    backgroundColor: '#EEF2FF',
                                    color: '#5965A6',
                                    fontWeight: 600,
                                  },
                                }}
                              >
                                <ListItemIcon
                                  sx={{
                                    minWidth: 24,
                                    color: '#5965A6',
                                    visibility:
                                      orgFilter === orgName
                                        ? 'visible'
                                        : 'hidden',
                                  }}
                                >
                                  <CheckRounded fontSize="small" />
                                </ListItemIcon>
                                {orgName}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    )}

                    <FormControl size="small" sx={{ minWidth: 180 }}>
                      <InputLabel sx={{ fontSize: '0.875rem' }}>
                        Status
                      </InputLabel>
                      <Select
                        value={statusFilter}
                        onChange={(e) => {
                          setStatusFilter(e.target.value);
                          setShowAll(false);
                        }}
                        label="Status"
                        IconComponent={KeyboardArrowDownRounded}
                        MenuProps={{
                          TransitionComponent: Fade,
                          PaperProps: {
                            sx: {
                              mt: 0.5,
                              borderRadius: '10px',
                              border: '1px solid #e5e7eb',
                              boxShadow: '0 10px 24px rgba(0,0,0,0.12)',
                              overflow: 'hidden',
                            },
                          },
                          MenuListProps: { dense: true, sx: { py: 0.5 } },
                        }}
                        sx={{
                          fontSize: '0.875rem',
                          borderRadius: '10px',
                          bgcolor: '#fff',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#e2e8f0',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#5965A6',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#8E98CD',
                            boxShadow: '0 0 0 2px rgba(89,101,166,0.14)',
                          },
                          '& .MuiSelect-select': {
                            py: 0.75,
                            px: 1.25,
                            fontSize: '0.75rem',
                          },
                          '& .MuiSelect-icon': { color: '#94a3b8' },
                          '&.Mui-expanded .MuiSelect-icon': {
                            transform: 'rotate(180deg)',
                          },
                        }}
                      >
                        {[
                          { v: 'all', label: 'All' },
                          { v: 'active', label: 'Active' },
                          { v: 'inactive', label: 'Inactive' },
                        ].map((opt) => (
                          <MenuItem
                            key={opt.v}
                            value={opt.v}
                            sx={{
                              fontSize: '0.875rem',
                              px: 1.25,
                              py: 0.75,
                              '&:hover': { backgroundColor: '#F3F6FF' },
                              '&.Mui-selected, &.Mui-selected:hover': {
                                backgroundColor: '#EEF2FF',
                                color: '#5965A6',
                                fontWeight: 600,
                              },
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: 24,
                                color: '#5965A6',
                                visibility:
                                  statusFilter === opt.v ? 'visible' : 'hidden',
                              }}
                            >
                              <CheckRounded fontSize="small" />
                            </ListItemIcon>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <AnimatePresence initial={false}>
                      {searchOpen ? (
                        <motion.div
                          key="searchbar"
                          initial={{ width: 0, opacity: 0 }}
                          animate={{ width: 260, opacity: 1 }}
                          exit={{ width: 0, opacity: 0 }}
                          transition={{ type: 'tween', duration: 0.25 }}
                        >
                          <TextField
                            autoFocus
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search users…"
                            size="small"
                            fullWidth
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon
                                    fontSize="small"
                                    sx={{ color: '#6B7280' }}
                                  />
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setSearch('');
                                      setSearchOpen(false);
                                    }}
                                    edge="end"
                                  >
                                    <CloseRounded sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                height: 34,
                                borderRadius: '8px',
                                background: '#F8FAFC',
                                fontSize: '0.75rem',
                              },
                            }}
                          />
                        </motion.div>
                      ) : (
                        <motion.div
                          key="searchicon"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                        >
                          <Tooltip title="Search users">
                            <IconButton
                              onClick={() => setSearchOpen(true)}
                              size="small"
                              sx={{
                                border: '1px solid rgba(79, 172, 254, 0.2)',
                                background: '#EEF2FF',
                                color: '#5965A6',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                                transition:
                                  'transform .18s cubic-bezier(.2,.8,.2,1), box-shadow .2s ease, background-color .2s ease',
                                '&:hover': {
                                  background: '#E5E9FF',
                                  transform: 'translateY(-1px) scale(1.02)',
                                  boxShadow: '0 6px 16px rgba(0,0,0,0.16)',
                                },
                                '&:active': {
                                  transform: 'translateY(0) scale(.99)',
                                  boxShadow: '0 3px 8px rgba(0,0,0,0.12)',
                                },
                                '&:focus-visible': {
                                  outline: 'none',
                                  boxShadow: '0 0 0 3px rgba(89,101,166,0.35)',
                                },
                              }}
                            >
                              <SearchIcon />
                            </IconButton>
                          </Tooltip>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <motion.div whileTap={{ scale: 0.98 }}>
                      <Button
                        variant="contained"
                        onClick={() =>
                          setDialog({ open: true, mode: 'create', user: null })
                        }
                        startIcon={
                          <Add
                            sx={{
                              fontSize: 20,
                              fontWeight: 600,
                              stroke: '#3A4DA8',
                              strokeWidth: 0.5,
                            }}
                          />
                        }
                        sx={{
                          fontSize: '0.875rem',
                          backgroundColor: '#5965A6',
                          padding: '6px 14px',
                          color: 'white',
                          textTransform: 'none',
                          textWrap: 'nowrap',
                          borderRadius: '6px',
                          fontWeight: 500,
                          boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                          transition:
                            'transform 180ms cubic-bezier(.2,.8,.2,1), box-shadow 200ms ease',
                          willChange: 'transform, box-shadow',
                          '&:hover': {
                            backgroundColor: '#4C5B9A',
                            transform: 'translateY(-1px) scale(1.02)',
                            boxShadow: '0 6px 16px rgba(0,0,0,0.16)',
                          },
                          '&:active': {
                            transform: 'translateY(0) scale(0.99)',
                            boxShadow: '0 3px 8px rgba(0,0,0,0.12)',
                          },
                          '&:focus-visible': {
                            outline: 'none',
                            boxShadow: '0 0 0 3px rgba(89,101,166,0.35)',
                          },
                        }}
                      >
                        New User
                      </Button>
                    </motion.div>

                    {filtered.length > 10 && (
                      <motion.div
                        whileTap={{ scale: 0.95 }}
                        onClick={() => setShowAll(!showAll)}
                      >
                        <Typography
                          sx={{
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            color: '#5965A6',
                            cursor: 'pointer',
                            px: 2,
                            py: 1,
                            borderRadius: '8px',
                            border: '1px solid rgba(79, 172, 254, 0.2)',
                            background: '#EEF2FF',
                            textWrap: 'nowrap',
                            userSelect: 'none',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                            '&:hover': {
                              boxShadow: '0 6px 16px rgba(0,0,0,0.16)',
                            },
                            '&:active': {
                              boxShadow: '0 3px 8px rgba(0,0,0,0.12)',
                            },
                            '&:focus-visible': {
                              outline: 'none',
                              boxShadow: '0 0 0 3px rgba(89,101,166,0.35)',
                            },
                          }}
                        >
                          {showAll
                            ? 'View Less'
                            : `View All (${filtered.length})`}
                        </Typography>
                      </motion.div>
                    )}
                  </Box>
                  {/* Loading Indicator */}
                  {loading && (
                    <CircularProgress size={24} sx={{ color: '#5965A6' }} />
                  )}
                </Box>
              </motion.div>

              {/* Table */}
              <TableContainer
                sx={{ overflow: 'hidden', paddingBottom: '1rem' }}
              >
                <Table
                  size="small"
                  sx={{
                    width: '100%',
                    '& .MuiTableCell-root': {
                      py: 0.5,
                      px: 2,
                      borderBottom: '1px solid #e5e7eb',
                    },
                    '& thead .MuiTableCell-root': { py: 0.5, px: 1.25 },
                    '& .MuiTableRow-root': { height: 44 },
                  }}
                >
                  <TableHead sx={{ backgroundColor: '#5965A6' }}>
                    <TableRow
                      sx={{
                        '&:hover': { backgroundColor: '#4C5B9A' },
                        transition: 'background-color 0.2s ease',
                      }}
                    >
                      <TableCell sx={thSx}>Sr No</TableCell>
                      {[
                        { label: 'Name', key: 'name' },
                        { label: 'Email', key: 'email' },
                        ...(isSuperAdmin
                          ? [{ label: 'Organization', key: 'organization' }]
                          : []),
                        { label: 'Role', key: 'role' },
                        { label: 'Status', key: 'status' },
                        ...(isSuperAdmin
                          ? [{ label: 'Trial Status', key: 'trial_status' }]
                          : []),
                        { label: 'Created', key: 'createdAt' },
                      ].map((h) => (
                        <TableCell
                          key={h.key}
                          onClick={() => handleSort(h.key)}
                          sx={thSxClickable}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                            }}
                          >
                            {h.label}
                            {sortConfig.column === h.key &&
                              sortConfig.direction === 'asc' && (
                                <ArrowUpwardIcon
                                  sx={{ fontSize: '0.875rem', ml: 0.5 }}
                                />
                              )}
                            {sortConfig.column === h.key &&
                              sortConfig.direction === 'desc' && (
                                <ArrowDownwardIcon
                                  sx={{ fontSize: '0.875rem', ml: 0.5 }}
                                />
                              )}
                            {(!sortConfig.column ||
                              sortConfig.column !== h.key) && (
                              <UnfoldMoreIcon
                                sx={{
                                  fontSize: '0.875rem',
                                  ml: 0.5,
                                  opacity: 0.5,
                                }}
                              />
                            )}
                          </Box>
                        </TableCell>
                      ))}
                      <TableCell sx={thSx}>Actions</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {displayed.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={isSuperAdmin ? 8 : 7}
                          align="center"
                          sx={{ py: 4 }}
                        >
                          <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.3 }}
                          >
                            <Typography
                              sx={{
                                color: '#64748b',
                                fontSize: '0.95rem',
                                mb: 1,
                              }}
                            >
                              No users found.
                            </Typography>
                            <Typography
                              sx={{ color: '#94a3b8', fontSize: '0.85rem' }}
                            >
                              Click &quot;Add New&quot; to create a user.
                            </Typography>
                          </motion.div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      <AnimatePresence>
                        {displayed.map((u, i) => (
                          <TableRow
                            key={u.user_id}
                            component={motion.tr}
                            custom={i}
                            variants={rowVariants}
                            initial="hidden"
                            animate="visible"
                            sx={{
                              borderBottom: '1px solid #F3F4F6',
                              backgroundColor:
                                i % 2 === 1 ? '#F3F5F7' : '#ffffff',
                              '&:hover': {
                                backgroundColor:
                                  i % 2 === 1 ? '#71B9FF40' : '#71B9FF40',
                              },
                            }}
                          >
                            <TableCell sx={tdIndexSx}>{i + 1}</TableCell>
                            <TableCell sx={tdTextSx}>
                              {u.first_name} {u.last_name}
                            </TableCell>
                            <TableCell
                              sx={{
                                py: 0.25,
                                px: 2,
                                fontSize: '0.875rem',
                                color: '#6b7280',
                              }}
                            >
                              {u.email}
                            </TableCell>
                            {isSuperAdmin && (
                              <TableCell sx={tdTextSx}>
                                {u.org_name || 'No Organization'}
                              </TableCell>
                            )}
                            <TableCell sx={{ py: 0.25, px: 2 }}>
                              <Pill color="#6b7280">{u.role_name}</Pill>
                            </TableCell>
                            <TableCell sx={{ py: 0.25, px: 2 }}>
                              <Pill
                                color={
                                  u.is_active === true ? '#2e7d32' : '#ed6c02'
                                }
                              >
                                {u.is_active ? 'Active' : 'Inactive'}
                              </Pill>
                            </TableCell>
                            {isSuperAdmin && (
                              <TableCell sx={{ py: 0.25, px: 2 }}>
                                {(() => {
                                  const hasOrg =
                                    u.org_id || u.organizationid || u.org_name;
                                  if (hasOrg) {
                                    return (
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: '#6b7280',
                                          fontSize: '0.75rem',
                                          fontStyle: 'italic',
                                        }}
                                      >
                                        See Organization
                                      </Typography>
                                    );
                                  }
                                  if (!u.trial_end_date) {
                                    return <Pill color="#d32f2f">Expired</Pill>;
                                  }
                                  const endDate = new Date(u.trial_end_date);
                                  const now = new Date();
                                  const daysRemaining = Math.ceil(
                                    (endDate - now) / (1000 * 60 * 60 * 24),
                                  );
                                  if (daysRemaining <= 0) {
                                    return <Pill color="#d32f2f">Expired</Pill>;
                                  }
                                  const color =
                                    daysRemaining <= 4 ? '#ed6c02' : '#2e7d32';
                                  return (
                                    <Pill color={color}>
                                      {`${daysRemaining} days left`}
                                    </Pill>
                                  );
                                })()}
                              </TableCell>
                            )}
                            <TableCell
                              sx={{
                                py: 0.25,
                                px: 2,
                                fontSize: '0.875rem',
                                color: '#6b7280',
                              }}
                            >
                              {u.created_at
                                ? formatDate(u.created_at, {
                                    includeTime: true,
                                  })
                                : 'N/A'}
                            </TableCell>
                            <TableCell sx={{ py: 0.25, px: 2 }}>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Tooltip title="Edit User">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setDialog({
                                        open: true,
                                        mode: 'edit',
                                        user: u,
                                      });
                                    }}
                                    sx={{
                                      color: '#6b7280',
                                      p: '6px',
                                      borderRadius: '6px',
                                      '&:hover': {
                                        backgroundColor: '#f3f4f6',
                                      },
                                    }}
                                  >
                                    <img
                                      src={EditIcon}
                                      alt="Edit"
                                      style={{ width: 22, height: 22 }}
                                    />
                                  </IconButton>
                                </Tooltip>
                                {isSuperAdmin && (
                                  <Tooltip title="Assign Organization">
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setAssignOrgDialog({
                                          open: true,
                                          user: u,
                                        });
                                      }}
                                      sx={{
                                        color: '#5965A6',
                                        p: '6px',
                                        borderRadius: '6px',
                                        '&:hover': {
                                          backgroundColor: '#EEF2FF',
                                        },
                                      }}
                                    >
                                      <Business sx={{ fontSize: 20 }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {/* <IconButton
                                  size="small"
                                  sx={{
                                    color: '#6b7280',
                                    p: '6px',
                                    borderRadius: '6px',
                                  }}
                                >
                                  <img
                                    src={DeleteIcon}
                                    alt="Delete"
                                    style={{ width: 22, height: 22 }}
                                  />
                                </IconButton> */}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </AnimatePresence>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </motion.div>
        </Box>
      </Box>

      <UserDialog
        open={dialog.open}
        mode={dialog.mode}
        user={dialog.user}
        onClose={() => setDialog({ open: false, mode: 'create', user: null })}
        onSubmit={async (data) => {
          if (dialog.mode === 'create') {
            await fetchUsersData();
            await fetchOrgSeatInfo(); // Refresh seat info after creating user
          } else {
            const userId =
              data.user_id ||
              data.id ||
              dialog.user?.user_id ||
              dialog.user?.id;
            if (userId) {
              setUsers((prev) =>
                prev.map((u) =>
                  u.user_id === userId || u.id === userId
                    ? { ...u, ...data }
                    : u,
                ),
              );
            }

            await fetchUsersData();
            await fetchOrgSeatInfo();
          }
        }}
      />

      <AssignOrganizationDialog
        open={assignOrgDialog.open}
        user={assignOrgDialog.user}
        onClose={() => setAssignOrgDialog({ open: false, user: null })}
        onSuccess={async () => {
          await fetchUsersData();
          await fetchOrgSeatInfo(); // Refresh seat info after user operations
        }}
      />
    </>
  );
}

const thSx = {
  fontSize: '0.75rem',
  fontWeight: 500,
  py: 1,
  px: 2,
  color: '#ffffff',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  borderBottom: '1px solid #E5E7EB',
};
const thSxClickable = {
  ...thSx,
  cursor: 'pointer',
  userSelect: 'none',
  position: 'relative',
  '&:hover': {
    backgroundColor: 'rgba(255,255,255,0.1)',
    transition: 'background-color 0.2s ease',
  },
};
const tdIndexSx = {
  py: 0.25,
  px: 2,
  fontSize: '0.875rem',
  fontWeight: 500,
  color: '#6b7280',
  verticalAlign: 'middle',
};
const tdTextSx = () => ({ py: 0.25, px: 2 });
